import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Button from "@/components/site/Button";
import SocialLinks from "@/components/site/SocialLinks";
import useSeo from "@/hooks/useSeo";
import { breadcrumbLd } from "@/lib/jsonld";
import { usePageContent, ContentLoading } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const ONDERWERPEN = ["Algemene vraag", "Avond workshop", "Zakelijk / BG5", "Iets anders"];
const QUERY_MAP = {
    workshop: "Avond workshop",
    zakelijk: "Zakelijk / BG5",
    bg5: "Zakelijk / BG5",
};

// Terugval-teksten: alleen gebruikt als de content-API faalt of te traag is.
const DEFAULTS = {
    "contact.seo-title": "Contact | Human Design Studio",
    "contact.seo-description":
        "Een vraag of even overleggen? Neem contact op met Marcel van der Veen.",
    "contact.eyebrow": "Contact",
    "contact.h1": "Contact",
    "contact.lead": "Een vraag, of wil je even overleggen wat past? Laat het weten.",
    "contact.label-telefoon": "Telefoon",
    "contact.telefoon": "06‑46090104",
    "contact.label-email": "E-mail",
    "contact.email": "info@humandesignstudio.nl",
    "contact.note": "Sessies zijn online. Live afspreken in Friesland kan ook, vraag ernaar.",
    "contact.social-label": "Volg mij",
    "contact.form.submit": "Verstuur",
    "contact.bevestiging.heading-a": "Bedankt, je bericht is",
    "contact.bevestiging.heading-accent": "verstuurd",
    "contact.bevestiging.text": "Ik neem zo snel mogelijk contact met je op.",
    "contact.bevestiging.knop": "Terug naar Home",
};

export default function Contact() {
    const c = usePageContent("contact", DEFAULTS);
    useSeo({
        title: c ? c.t("contact.seo-title") : DEFAULTS["contact.seo-title"],
        description: c
            ? c.t("contact.seo-description")
            : DEFAULTS["contact.seo-description"],
        path: "/contact",
        jsonLd: [breadcrumbLd("Contact", "/contact")],
    });
    const mountRef = useRef(Date.now());
    const [params] = useSearchParams();
    const initialOnderwerp = QUERY_MAP[(params.get("onderwerp") || "").toLowerCase()] || "Algemene vraag";

    const [form, setForm] = useState({
        naam: "",
        email: "",
        telefoon: "",
        onderwerp: initialOnderwerp,
        bericht: "",
        privacy_akkoord: false,
        website: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [serverError, setServerError] = useState("");

    const validate = () => {
        const e = {};
        if (!form.naam.trim()) e.naam = "Vul je naam in.";
        if (!emailOk(form.email)) e.email = "Vul een geldig e-mailadres in.";
        if (!form.bericht.trim()) e.bericht = "Vul je bericht in.";
        if (!form.privacy_akkoord) e.privacy_akkoord = "Je moet akkoord gaan om te versturen.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        setServerError("");
        try {
            await axios.post(`${API}/contact-messages`, {
                ...form,
                elapsed_ms: Date.now() - mountRef.current,
            });
            setDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setServerError(typeof d === "string" ? d : "Er ging iets mis. Probeer het opnieuw.");
        } finally {
            setSubmitting(false);
        }
    };

    if (!c) return <ContentLoading />;

    if (done) {
        return (
            <section data-testid="contact-confirmation" className="mx-auto max-w-[720px] px-5 md:px-10 pt-28 md:pt-40 pb-32 min-h-[70vh]">
                <div className="w-16 h-16 rounded-full bg-[var(--hds-green)] flex items-center justify-center text-[var(--hds-card)] mb-10">
                    <Check size={28} strokeWidth={1.5} />
                </div>
                <h1 className="font-[Fraunces] text-[40px] md:text-[56px] leading-[1.02] tracking-[-0.02em]" style={{ fontWeight: 300 }}>
                    {c.t("contact.bevestiging.heading-a")}{" "}
                    <em className="italic text-[var(--hds-clay)]" style={{ fontWeight: 300 }}>{c.t("contact.bevestiging.heading-accent")}</em>
                </h1>
                <p className="mt-6 text-[17px] leading-[1.6] text-[var(--hds-ink)]/80 max-w-[48ch]">
                    {c.t("contact.bevestiging.text")}
                </p>
                <div className="mt-10">
                    <Button to="/" variant="secondary">{c.t("contact.bevestiging.knop")}</Button>
                </div>
            </section>
        );
    }

    return (
        <section data-testid="page-contact" className="mx-auto max-w-[1100px] px-5 md:px-10 pt-24 md:pt-40 pb-24">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
                {/* Left: intro + direct details */}
                <div className="md:col-span-5">
                    <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)]/90 mb-6">
                        {c.t("contact.eyebrow")}
                    </motion.p>
                    <h1 className="font-[Fraunces] text-[44px] md:text-[64px] leading-[1] tracking-[-0.02em]" style={{ fontWeight: 300 }}>
                        {c.t("contact.h1")}
                    </h1>
                    <p className="mt-6 font-[Fraunces] text-[22px] md:text-[26px] leading-[1.25] text-[var(--hds-ink)]" style={{ fontWeight: 300 }}>
                        {c.t("contact.lead")}
                    </p>

                    <div className="mt-10 space-y-5">
                        <div>
                            <p className="text-[11.5px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-1">{c.t("contact.label-telefoon")}</p>
                            <a href="tel:0646090104" data-testid="contact-phone" className="text-[18px] text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300">
                                {c.t("contact.telefoon")}
                            </a>
                        </div>
                        <div>
                            <p className="text-[11.5px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-1">{c.t("contact.label-email")}</p>
                            <a href="mailto:info@humandesignstudio.nl" data-testid="contact-email" className="text-[18px] text-[var(--hds-ink)] hover:text-[var(--hds-clay)] transition-colors duration-300 break-all">
                                {c.t("contact.email")}
                            </a>
                        </div>
                        <p className="text-[15px] leading-[1.6] text-[var(--hds-ink)]/70 max-w-[42ch] pt-2">
                            {c.t("contact.note")}
                        </p>
                        <div data-testid="contact-social" className="pt-4">
                            <p className="text-[11.5px] tracking-[0.2em] uppercase text-[var(--hds-ink)]/50 mb-3">
                                {c.t("contact.social-label")}
                            </p>
                            <SocialLinks testIdPrefix="contact-social" />
                        </div>
                    </div>
                </div>

                {/* Right: form */}
                <div className="md:col-span-7">
                    <div className="rounded-[16px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-7 md:p-9">
                        {/* honeypot */}
                        <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden data-testid="contact-website" />

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <FormField label="Naam" name="naam" value={form.naam} error={errors.naam} form={form} onChange={setForm} testid="contact-naam" />
                            <FormField label="E-mailadres" name="email" type="email" value={form.email} error={errors.email} form={form} onChange={setForm} testid="contact-email-field" />
                            <FormField label="Telefoon (optioneel)" name="telefoon" value={form.telefoon} error={null} form={form} onChange={setForm} testid="contact-telefoon" />
                            <div>
                                <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">Onderwerp</label>
                                <select data-testid="contact-onderwerp" value={form.onderwerp} onChange={(e) => setForm({ ...form, onderwerp: e.target.value })} className="w-full rounded-[12px] bg-[var(--hds-sand)] border border-[var(--hds-line)] focus:border-[var(--hds-clay)] outline-none px-4 py-3 text-[15px] transition-colors duration-300">
                                    {ONDERWERPEN.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">Bericht</label>
                            <textarea data-testid="contact-bericht" rows={5} value={form.bericht} onChange={(e) => setForm({ ...form, bericht: e.target.value })} className={`w-full rounded-[12px] bg-[var(--hds-sand)] border outline-none px-4 py-3 text-[15px] transition-colors duration-300 ${errors.bericht ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"}`} />
                            {errors.bericht && <p className="mt-1.5 text-[13px] text-[var(--hds-clay-dark)]" data-testid="contact-error-bericht">{errors.bericht}</p>}
                        </div>

                        <label className="mt-5 flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" data-testid="contact-privacy" checked={form.privacy_akkoord} onChange={(e) => setForm({ ...form, privacy_akkoord: e.target.checked })} className="mt-1 w-5 h-5 accent-[var(--hds-clay)] shrink-0" />
                            <span className="text-[14px] leading-[1.55] text-[var(--hds-ink)]/80">
                                Ik ga akkoord met de{" "}
                                <Link to="/privacy" className="text-[var(--hds-clay)] underline underline-offset-2">privacyverklaring</Link>.
                            </span>
                        </label>
                        {errors.privacy_akkoord && <p className="mt-2 text-[13px] text-[var(--hds-clay-dark)]" data-testid="contact-error-privacy">{errors.privacy_akkoord}</p>}

                        {serverError && <p className="mt-4 text-[14px] text-[var(--hds-clay-dark)]" data-testid="contact-server-error">{serverError}</p>}

                        <div className="mt-7">
                            <Button variant="primary" icon="arrow" onClick={submit} disabled={submitting} data-testid="contact-submit">
                                {submitting ? "Bezig..." : c.t("contact.form.submit")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

const FormField = ({ label, name, type = "text", value, error, form, onChange, testid }) => (
    <div>
        <label className="block text-[13px] tracking-[0.12em] uppercase text-[var(--hds-ink)]/55 mb-2">{label}</label>
        <input
            type={type}
            data-testid={testid}
            value={value}
            onChange={(e) => onChange({ ...form, [name]: e.target.value })}
            className={`w-full rounded-[12px] bg-[var(--hds-sand)] border outline-none px-4 py-3 text-[15px] transition-colors duration-300 ${error ? "border-[var(--hds-clay)]" : "border-[var(--hds-line)] focus:border-[var(--hds-clay)]"}`}
        />
        {error && <p className="mt-1.5 text-[13px] text-[var(--hds-clay-dark)]" data-testid={`${testid}-error`}>{error}</p>}
    </div>
);
